/* Product detail page.
   - Gallery: two different layouts share the same markup/JS.
       * Desktop (>=990px): ".product-detail__gallery-wrap" is stretched to
         N screens tall (--gallery-count, set inline per-product by the
         Liquid template) and ".product-detail__gallery" itself is
         "position: sticky" so it stays pinned to the viewport the whole
         time the page scrolls through that tall wrap -- only one photo is
         ever on screen. Every ".product-detail__slide" is stacked on top
         of each other (absolute + inset:0) and crossfades via opacity/
         ".is-active" as the user scrolls, instead of the old plain
         vertical stack (which let the next photo's top edge peek into
         view immediately below the first one). Since every slide now
         occupies the exact same on-screen rect while pinned, a plain
         IntersectionObserver can no longer tell which one is "current" --
         intersection ratio is about geometry, not CSS opacity, and they
         all geometrically overlap the sticky container 100% of the time.
         So on desktop, "which photo is active" is driven instead by how
         far the page has scrolled through the wrap (updateFromScrollProgress).
       * Mobile (<990px): resets back to a normal in-flow horizontal
         scroll-snap slider (component-main-product.css), where an
         IntersectionObserver watching each slide's real, distinct
         intersection ratio works correctly -- so that code path is kept
         for mobile only.
   - Variants: color swatches / size pills reuse the same matching logic
     already proven out in assets/quick-view.js (findVariantForOptions /
     variantExistsAndAvailable), just reading from the section's own
     embedded "{{ product.variants | json }}" script tag instead of an
     AJAX fetch, since Liquid already has the product in scope server-side
     here. Selecting a variant updates the price, stock text, swatch/size
     button states, and the hidden "id" input product-form.js submits --
     it does not touch the gallery's scroll position except to jump to a
     newly-selected color's cover photo (data-jump-index, computed
     server-side from variant.featured_image.position). */
(function () {
  const DESKTOP_MQL = window.matchMedia('(min-width: 990px)');

  function initGallery(root) {
    const thumbsWrap = root.querySelector('[data-product-thumbs]');
    const galleryWrap = root.querySelector('[data-product-gallery]');
    if (!thumbsWrap || !galleryWrap) return null;

    const thumbs = Array.from(thumbsWrap.querySelectorAll('[data-thumb-index]'));
    const slides = Array.from(galleryWrap.querySelectorAll('[data-gallery-slide]'));
    if (!slides.length) return null;

    // Mobile-only slide dots (component-main-product.css turns the gallery
    // into a horizontal scroll-snap slider below 990px) -- optional, only
    // rendered by the Liquid template when there's more than one photo.
    const dotsWrap = root.querySelector('[data-product-dots]');
    const dots = dotsWrap ? Array.from(dotsWrap.querySelectorAll('[data-dot-index]')) : [];

    // The outer wrap: on desktop this is the N-screens-tall scroll track
    // that ".product-detail__gallery" stays pinned inside of. Mobile-only
    // prev/next arrows over the horizontal slider also live here (desktop
    // keeps its thumb rail instead and never renders these).
    const galleryWrapEl = root.querySelector('.product-detail__gallery-wrap');
    const prevArrow = galleryWrapEl ? galleryWrapEl.querySelector('[data-gallery-prev]') : null;
    const nextArrow = galleryWrapEl ? galleryWrapEl.querySelector('[data-gallery-next]') : null;

    let currentIndex = 0;

    function setActive(index) {
      currentIndex = index;
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
    }

    function isDesktopStage() {
      return DESKTOP_MQL.matches && slides.length > 1 && !!galleryWrapEl;
    }

    // Desktop: translate "how far has the page scrolled through the tall
    // wrap" into "which of the N slides is current" and crossfade to it.
    function updateFromScrollProgress() {
      if (!isDesktopStage()) return;
      const rect = galleryWrapEl.getBoundingClientRect();
      const scrollableDistance = rect.height - window.innerHeight;
      if (scrollableDistance <= 0) {
        setActive(0);
        return;
      }
      const scrolled = Math.min(Math.max(-rect.top, 0), scrollableDistance);
      const progress = scrolled / scrollableDistance;
      const index = Math.min(slides.length - 1, Math.floor(progress * slides.length));
      setActive(index);
    }

    window.addEventListener('scroll', updateFromScrollProgress, { passive: true });
    window.addEventListener('resize', updateFromScrollProgress);
    DESKTOP_MQL.addEventListener
      ? DESKTOP_MQL.addEventListener('change', updateFromScrollProgress)
      : DESKTOP_MQL.addListener(updateFromScrollProgress);
    updateFromScrollProgress();

    // Mobile: real IntersectionObserver over the horizontal in-flow
    // slider, exactly as before. Guarded so it never fights the desktop
    // scroll-progress logic above (every slide geometrically overlaps
    // 100% on desktop since they're stacked in the same pinned rect, so
    // this would otherwise just get stuck on whichever slide's callback
    // happened to fire first).
    const visibleRatios = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        if (isDesktopStage()) return;
        entries.forEach((entry) => {
          visibleRatios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
        });
        let bestIndex = -1;
        let bestRatio = 0;
        slides.forEach((slide, i) => {
          const ratio = visibleRatios.get(slide) || 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestIndex = i;
          }
        });
        if (bestIndex > -1) setActive(bestIndex);
      },
      { root: null, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    slides.forEach((slide) => observer.observe(slide));

    // Scrolls the page/slider to a given slide index, branching per layout:
    // desktop scrolls the *page* to the point in the tall wrap where that
    // slide's segment starts; mobile just scrolls the slide into view
    // inside its own horizontal scroll container (unchanged from before).
    function scrollToIndex(index) {
      if (!slides[index]) return;
      if (isDesktopStage()) {
        const rect = galleryWrapEl.getBoundingClientRect();
        const scrollableDistance = rect.height - window.innerHeight;
        const targetProgress = index / slides.length;
        const wrapTopAbsolute = window.scrollY + rect.top;
        const targetY = wrapTopAbsolute + scrollableDistance * targetProgress + 1;
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      } else {
        slides[index].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
      }
    }

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => scrollToIndex(i));
    });

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => scrollToIndex(i));
    });

    if (prevArrow) {
      prevArrow.addEventListener('click', () => scrollToIndex(Math.max(currentIndex - 1, 0)));
    }

    if (nextArrow) {
      nextArrow.addEventListener('click', () => scrollToIndex(Math.min(currentIndex + 1, slides.length - 1)));
    }

    return {
      jumpTo(index) {
        scrollToIndex(index);
      },
    };
  }

  /* The thumbnail rail is "position: fixed" so it stays pinned to the
     viewport corner throughout the gallery scroll. That's exactly what
     was asked for, but a truly fixed element has no concept of "my
     section ended" -- it keeps rendering on top of whatever comes after
     ".product-detail" once the page scrolls past it. Toggle it off with
     a plain scroll listener instead of fighting CSS for this: once
     ".product-detail" itself has scrolled fully out of view (its bottom
     edge is at or above the top of the viewport), hide the rail; bring
     it back if the user scrolls back up into the section. */
  function initThumbVisibility(root) {
    const thumbsWrap = root.querySelector('[data-product-thumbs]');
    if (!thumbsWrap) return;

    function update() {
      const rect = root.getBoundingClientRect();
      thumbsWrap.classList.toggle('is-past-section', rect.bottom <= 0);
    }

    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  function readJSON(root, selector) {
    const el = root.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  }

  function findVariantForOptions(variants, numOptions, overrides, currentVariant) {
    const exact = variants.find((v) => {
      for (let i = 0; i < numOptions; i++) {
        const key = 'option' + (i + 1);
        if (Object.prototype.hasOwnProperty.call(overrides, i)) {
          if (v[key] !== overrides[i]) return false;
        } else if (currentVariant && v[key] !== currentVariant[key]) {
          return false;
        }
      }
      return true;
    });
    if (exact) return exact;
    return variants.find((v) => Object.keys(overrides).every((i) => v['option' + (Number(i) + 1)] === overrides[i]));
  }

  function variantExistsAndAvailable(variants, numOptions, overrides) {
    const v = variants.find((variant) => {
      for (let i = 0; i < numOptions; i++) {
        const key = 'option' + (i + 1);
        if (Object.prototype.hasOwnProperty.call(overrides, i) && variant[key] !== overrides[i]) return false;
      }
      return true;
    });
    return !!(v && v.available);
  }

  function money(cents) {
    return typeof formatMoney === 'function' ? formatMoney(cents, window.themeMoneyFormat) : '$' + (cents / 100).toFixed(2);
  }

  function priceHtml(variant) {
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      return (
        '<dl><div class="price__sale">' +
        '<dd class="price__compare"><span class="price-item price-item--regular">' + money(variant.compare_at_price) + '</span></dd>' +
        '<dd class="price__last"><span class="price-item price-item--sale">' + money(variant.price) + '</span></dd>' +
        '</div></dl>'
      );
    }
    return (
      '<dl><div class="price__regular">' +
      '<dd><span class="price-item price-item--regular">' + money(variant.price) + '</span></dd>' +
      '</div></dl>'
    );
  }

  function initVariants(root, gallery) {
    const variants = readJSON(root, '[data-product-json]');
    const options = readJSON(root, '[data-product-options-json]');
    if (!variants || !options) return;

    const colorIndex = options.colorOptionIndex;
    const sizeIndex = options.sizeOptionIndex;
    const numOptions = (colorIndex > -1 ? 1 : 0) + (sizeIndex > -1 ? 1 : 0) > 0 ? Math.max(colorIndex, sizeIndex) + 1 : 1;

    const idInput = root.querySelector('[data-product-variant-id]');
    let selectedVariant = variants.find((v) => String(v.id) === (idInput && idInput.value)) || variants.find((v) => v.available) || variants[0];

    const colorOptionKey = colorIndex > -1 ? 'option' + (colorIndex + 1) : null;
    const sizeOptionKey = sizeIndex > -1 ? 'option' + (sizeIndex + 1) : null;

    const swatches = Array.from(root.querySelectorAll('[data-color-value]'));
    const sizeButtons = Array.from(root.querySelectorAll('[data-size-value]'));
    const selectedColorLabels = Array.from(root.querySelectorAll('[data-product-selected-color]'));
    const priceEl = root.querySelector('[data-product-price]');
    // One badge per gallery slide now (not just the first photo), so this
    // has to update every one of them together instead of a single node.
    const badgeEls = Array.from(root.querySelectorAll('[data-product-badge]'));
    const badgeTextEls = Array.from(root.querySelectorAll('[data-product-badge-text]'));
    const stickyPriceEl = root.querySelector('[data-sticky-price]');
    const stickyImageEl = root.querySelector('[data-sticky-image]');
    const stockEl = root.querySelector('[data-product-stock]');
    const addToCartBtn = root.querySelector('[data-product-add-to-cart]');
    const stickyAddToCartBtn = root.querySelector('[data-sticky-add-to-cart]');

    function refreshSizeButtons() {
      if (!sizeOptionKey) return;
      sizeButtons.forEach((btn) => {
        const val = btn.dataset.sizeValue;
        const overrides = {};
        overrides[sizeIndex] = val;
        if (colorOptionKey) overrides[colorIndex] = selectedVariant[colorOptionKey];
        const isAvailable = variantExistsAndAvailable(variants, numOptions, overrides);
        btn.classList.toggle('is-selected', selectedVariant[sizeOptionKey] === val);
        btn.classList.toggle('is-unavailable', !isAvailable);
        btn.disabled = !isAvailable;
      });
    }

    function applyVariant(variant, opts) {
      if (!variant) return;
      const colorChanged = colorOptionKey && selectedVariant[colorOptionKey] !== variant[colorOptionKey];
      selectedVariant = variant;

      if (idInput) idInput.value = variant.id;

      if (colorOptionKey) {
        swatches.forEach((s) => s.classList.toggle('is-selected', s.dataset.colorValue === variant[colorOptionKey]));
        selectedColorLabels.forEach((label) => { label.textContent = variant[colorOptionKey]; });
      }

      refreshSizeButtons();

      if (priceEl) {
        priceEl.innerHTML = priceHtml(variant);
        priceEl.classList.toggle('price--on-sale', !!(variant.compare_at_price && variant.compare_at_price > variant.price));
        priceEl.classList.toggle('price--sold-out', !variant.available);
      }

      if (badgeEls.length) {
        const onSale = !!(variant.compare_at_price && variant.compare_at_price > variant.price);
        const percent = onSale
          ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
          : 0;
        badgeEls.forEach((badgeEl, i) => {
          const badgeTextEl = badgeTextEls[i];
          if (onSale) {
            badgeEl.classList.remove('hidden');
            if (badgeTextEl) {
              badgeTextEl.classList.add('badge--discount');
              badgeTextEl.textContent = 'Sale ' + percent + '%';
            }
          } else {
            const baseType = badgeEl.dataset.badgeBaseType;
            const baseText = badgeEl.dataset.badgeBaseText;
            badgeEl.classList.toggle('hidden', !baseType);
            if (badgeTextEl) {
              badgeTextEl.classList.remove('badge--discount');
              if (baseType) badgeTextEl.textContent = baseText;
            }
          }
        });
      }

      if (stickyPriceEl) stickyPriceEl.textContent = money(variant.price);

      if (stickyImageEl && colorOptionKey) {
        const swatchBtn = swatches.find((s) => s.dataset.colorValue === variant[colorOptionKey]);
        const swatchImg = swatchBtn && swatchBtn.querySelector('img');
        if (swatchImg) stickyImageEl.src = swatchImg.src;
      }

      if (stickyAddToCartBtn) {
        stickyAddToCartBtn.disabled = !variant.available;
        const stickyLabel = stickyAddToCartBtn.querySelector('span');
        if (stickyLabel) {
          stickyLabel.textContent = variant.available
            ? (window.productStrings && window.productStrings.addToCart) || 'Add to cart'
            : (window.productStrings && window.productStrings.soldOut) || 'Sold out';
        }
      }

      if (stockEl) {
        stockEl.classList.toggle('is-unavailable', !variant.available);
        if (variant.available) {
          stockEl.textContent =
            variant.inventory_management === 'shopify' && variant.inventory_quantity > 0
              ? (window.productStrings && window.productStrings.inStockCount
                  ? window.productStrings.inStockCount.replace('__COUNT__', variant.inventory_quantity)
                  : variant.inventory_quantity + ' in Stock, Ready to Ship')
              : (window.productStrings && window.productStrings.inStock) || 'In Stock, Ready to Ship';
        } else {
          stockEl.textContent = (window.productStrings && window.productStrings.soldOut) || 'Sold out';
        }
      }

      if (addToCartBtn) {
        addToCartBtn.disabled = !variant.available;
        const label = addToCartBtn.querySelector('span');
        if (label) {
          label.textContent = variant.available
            ? (window.productStrings && window.productStrings.addToCart) || 'Add to cart'
            : (window.productStrings && window.productStrings.soldOut) || 'Sold out';
        }
      }

      if (colorChanged && gallery && variant.featured_image && typeof variant.featured_image.position === 'number') {
        gallery.jumpTo(variant.featured_image.position - 1);
      } else if (!opts || !opts.skipJump) {
        const swatchBtn = colorOptionKey && swatches.find((s) => s.dataset.colorValue === variant[colorOptionKey]);
        if (colorChanged && gallery && swatchBtn && swatchBtn.dataset.jumpIndex) {
          gallery.jumpTo(parseInt(swatchBtn.dataset.jumpIndex, 10));
        }
      }
    }

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        if (swatch.disabled) return;
        const overrides = {};
        overrides[colorIndex] = swatch.dataset.colorValue;
        const variant = findVariantForOptions(variants, numOptions, overrides, selectedVariant);
        applyVariant(variant);
      });
    });

    sizeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const overrides = {};
        overrides[sizeIndex] = btn.dataset.sizeValue;
        const variant = findVariantForOptions(variants, numOptions, overrides, selectedVariant);
        applyVariant(variant, { skipJump: true });
      });
    });
  }

  /* Fabric & Care modal: the metafield is plain rich text ("Label: body."
     paragraphs, plus a "Care: Do not wash. Do not bleach. ..." paragraph).
     Re-parse that into the reference design: bold heading + gray body per
     material section, a divider, then the care instructions as an
     icon + label list. Runs once per modal (idempotent via a data flag)
     so it works for any product without requiring the metafield content
     itself to be restructured. */
  var CARE_ICON_SVG = {
    wash:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M4 4L12 12L4 20"/><path d="M20 4L12 12L20 20"/></svg>',
    bleach:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M12 4L20 19H4Z"/><path d="M6 15L18 15"/><path d="M5 19L19 5"/></svg>',
    iron:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M9 8H15L18 18H6Z"/><path d="M10 8V5H14V8"/><path d="M5 20L19 4"/></svg>',
    dryClean:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="8"/><path d="M6 6L18 18"/></svg>',
  };

  function careIconFor(text) {
    var t = text.toLowerCase();
    if (t.indexOf('dry clean') > -1) return CARE_ICON_SVG.dryClean;
    if (t.indexOf('bleach') > -1) return CARE_ICON_SVG.bleach;
    if (t.indexOf('iron') > -1) return CARE_ICON_SVG.iron;
    if (t.indexOf('wash') > -1) return CARE_ICON_SVG.wash;
    return CARE_ICON_SVG.wash;
  }

  function formatFabricCare(root) {
    var sectionId = root.dataset.sectionId;
    var modal = sectionId
      ? document.getElementById('FabricCareModal-' + sectionId)
      : document.querySelector('[id^="FabricCareModal-"]');
    if (!modal) return;
    var textEl = modal.querySelector('.banner-popup-modal__text');
    if (!textEl || textEl.dataset.fabricCareFormatted) return;
    textEl.dataset.fabricCareFormatted = 'true';

    var paragraphs = Array.from(textEl.querySelectorAll('p'))
      .map(function (p) { return p.textContent.replace(/\s+/g, ' ').trim(); })
      .filter(Boolean);
    if (!paragraphs.length) return;

    var html = '';
    var sawMaterialSection = false;

    paragraphs.forEach(function (line) {
      var colonIndex = line.indexOf(':');
      var label = colonIndex > -1 ? line.slice(0, colonIndex).trim() : '';
      var rest = colonIndex > -1 ? line.slice(colonIndex + 1).trim() : line;
      var isCare = /^care$/i.test(label) || /do not/i.test(line);

      if (isCare) {
        var items = rest
          .split(/(?<=[.])\s+/)
          .map(function (s) { return s.replace(/\.$/, '').trim(); })
          .filter(Boolean);
        if (sawMaterialSection) {
          html += '<hr class="fabric-care__divider">';
        }
        html += '<div class="fabric-care__section">';
        if (label) html += '<h4 class="fabric-care__heading">' + label + '</h4>';
        html += '<ul class="fabric-care__care-list">';
        items.forEach(function (item) {
          html +=
            '<li class="fabric-care__care-item">' +
            careIconFor(item) +
            '<span>' + item + '</span>' +
            '</li>';
        });
        html += '</ul></div>';
      } else {
        sawMaterialSection = true;
        html += '<div class="fabric-care__section">';
        if (label) html += '<h4 class="fabric-care__heading">' + label + '</h4>';
        html += '<p class="fabric-care__body">' + (rest || line) + '</p>';
        html += '</div>';
      }
    });

    textEl.innerHTML = html;
  }

  /* Shipping, Exchange & Returns modal: the metafield holds one paragraph
     per shipping method ("Title / Time / Price. Body sentence.") followed
     by one paragraph per return option ("Title: Body sentence."). Re-parse
     into: bold heading + gray body per item, with a divider + an
     "Exchange & Returns" section heading auto-inserted at the point where
     the content switches from shipping methods to return options. The
     final contact line is static markup already in the Liquid template,
     appended after this parsed block. */
  function formatShipping(root) {
    var sectionId = root.dataset.sectionId;
    var modal = sectionId
      ? document.getElementById('ShippingModal-' + sectionId)
      : document.querySelector('[id^="ShippingModal-"]');
    if (!modal) return;
    var textEl = modal.querySelector('.banner-popup-modal__text');
    if (!textEl || textEl.dataset.shippingFormatted) return;
    textEl.dataset.shippingFormatted = 'true';

    var paragraphs = Array.from(textEl.querySelectorAll('p'))
      .map(function (p) { return p.textContent.replace(/\s+/g, ' ').trim(); })
      .filter(Boolean);
    if (!paragraphs.length) return;

    var html = '';
    var sawShippingMethod = false;
    var insertedReturnsHeading = false;
    var exchangeReturnsLabel =
      (window.productStrings && window.productStrings.exchangeReturns) || 'Exchange & Returns';

    paragraphs.forEach(function (line) {
      var isShippingMethod = / \/ /.test(line);

      if (isShippingMethod) {
        sawShippingMethod = true;
        var match = line.match(/\.\s+/);
        var heading = match ? line.slice(0, match.index) : line;
        var body = match ? line.slice(match.index + match[0].length) : '';
        html += '<div class="fabric-care__section">';
        html += '<h4 class="fabric-care__heading">' + heading + '</h4>';
        if (body) html += '<p class="fabric-care__body">' + body + '</p>';
        html += '</div>';
      } else {
        if (sawShippingMethod && !insertedReturnsHeading) {
          insertedReturnsHeading = true;
          html += '<hr class="fabric-care__divider">';
          html += '<h3 class="fabric-care__heading" style="font-size:1.8rem;margin-bottom:1.2rem;">' + exchangeReturnsLabel + '</h3>';
        }
        var colonIndex = line.indexOf(':');
        var label = colonIndex > -1 ? line.slice(0, colonIndex).trim() : '';
        var rest = colonIndex > -1 ? line.slice(colonIndex + 1).trim() : line;
        html += '<div class="fabric-care__section">';
        if (label) html += '<h4 class="fabric-care__heading">' + label + '</h4>';
        html += '<p class="fabric-care__body">' + (rest || line) + '</p>';
        html += '</div>';
      }
    });

    textEl.innerHTML = html;
  }

  /* Sticky "add to cart" bar: appears once the visitor scrolls past the
     real Add to Cart button in the info column, and hides again once the
     site footer comes into view (so it never overlaps the footer). Just
     image/title/price (kept in sync elsewhere by data-sticky-image and
     data-sticky-price) and this proxy button -- no color swatches here. */
  function initStickyBar(root) {
    const bar = root.querySelector('[data-sticky-bar]');
    const mainAddBtn = root.querySelector('[data-product-add-to-cart]');
    if (!bar || !mainAddBtn) return;

    const footer = document.querySelector('.shopify-section-footer');

    function update() {
      const btnRect = mainAddBtn.getBoundingClientRect();
      const pastMainButton = btnRect.bottom < 0;
      const footerRect = footer ? footer.getBoundingClientRect() : null;
      const footerVisible = footerRect ? footerRect.top < window.innerHeight : false;
      bar.classList.toggle('is-visible', pastMainButton && !footerVisible);
    }

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    const stickyBtn = bar.querySelector('[data-sticky-add-to-cart]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        if (!mainAddBtn.disabled) mainAddBtn.click();
      });
    }
  }

  /* Click-to-zoom: clicking any gallery photo (".product-detail__slide-image",
     see sections/main-product.liquid) opens ".product-media-lightbox" on
     that same photo. The lightbox is a "modal-dialog" custom element
     (global.js) that moves itself to a direct child of <body> once
     connected, so it's queried from the document instead of "root" --
     it's a sibling of ".product-detail" in the rendered markup, not a
     descendant, by the time this runs. */
  function initMediaLightbox(root) {
    const modal = document.querySelector('.product-media-lightbox');
    if (!modal) return;

    const slides = Array.from(modal.querySelectorAll('[data-lightbox-slide]'));
    if (!slides.length) return;

    const prevBtn = modal.querySelector('[data-lightbox-prev]');
    const nextBtn = modal.querySelector('[data-lightbox-next]');

    function showSlide(index) {
      const clamped = ((index % slides.length) + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === clamped));
    }

    function currentIndex() {
      const active = slides.findIndex((slide) => slide.classList.contains('is-active'));
      return active > -1 ? active : 0;
    }

    const triggers = Array.from(root.querySelectorAll('[data-gallery-slide]'));
    triggers.forEach((slide) => {
      const img = slide.querySelector('.product-detail__slide-image');
      if (!img) return;
      img.addEventListener('click', () => {
        showSlide(parseInt(slide.dataset.index, 10) || 0);
        modal.show(img);
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', () => showSlide(currentIndex() - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => showSlide(currentIndex() + 1));
  }

  function init() {
    document.querySelectorAll('.product-detail').forEach((root) => {
      if (root.dataset.mainProductBound) return;
      root.dataset.mainProductBound = 'true';
      const gallery = initGallery(root);
      initVariants(root, gallery);
      initThumbVisibility(root);
      formatFabricCare(root);
      formatShipping(root);
      initStickyBar(root);
      initMediaLightbox(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
